import VideoStyles from "@components/cms/video/video.module.css";
import type { CSSObject } from "@emotion/react";
import type { BoxProps } from "@ecco/ui/box";
import type { FlexProps } from "@ecco/ui/flex";
import type { ButtonProps } from "@ecco/ui/button";
import { defaultUnderlineStyle } from "@ecco/ui/css/common";
import { aspectRatios, breakpoints, colors, space, typography } from "@ecco/ui/tokens";

const reactPlayer = VideoStyles["react-player"];

const fs = typography.fontSizes;

const { md, lg } = breakpoints;

const mq = (minWidth: string) => `@media (min-width: ${minWidth})` as const;

const heroRichTextLinkStyle = {
    color: "inherit",
    fontWeight: "bold",
    ...defaultUnderlineStyle,
    "&:hover": {
        color: colors.gray[500],
        textDecorationColor: colors.gray[500],
    },
};

const heroPadding = {
    mobileTablet: {
        px: "16px",
        py: space["4"],
        gap: space["4"],
    },
    desktop: {
        px: "32px",
        py: space["4.8"],
        gap: space["4.8"],
    },
} as const;

const mediaAspect = {
    base: aspectRatios["4:5"],
    tablet: aspectRatios["16:9"],
    desktop: aspectRatios["12:5"],
} as const;

export const outsideProps = {
    root: {
        width: "100%",
        boxSizing: "border-box",
        overflowX: "hidden",
        flexDirection: "column",
        backgroundColor: colors.white,
        css: {
            paddingLeft: heroPadding.mobileTablet.px,
            paddingRight: heroPadding.mobileTablet.px,
            paddingTop: 0,
            paddingBottom: heroPadding.mobileTablet.py,
            gap: heroPadding.mobileTablet.gap,
            [mq(lg)]: {
                paddingLeft: heroPadding.desktop.px,
                paddingRight: heroPadding.desktop.px,
                paddingTop: 0,
                paddingBottom: heroPadding.desktop.py,
                gap: heroPadding.desktop.gap,
            },
        } satisfies CSSObject,
    } satisfies FlexProps,

    mediaOuter: {
        width: "100%",
        boxSizing: "border-box",
        position: "relative",
    } satisfies BoxProps,

    mediaInner: {
        css: {
            position: "relative",
            width: "100%",
            overflow: "hidden",
            aspectRatio: mediaAspect.base,
            [mq(md)]: { aspectRatio: mediaAspect.tablet },
            [mq(lg)]: {
                aspectRatio: mediaAspect.desktop,
                minHeight: "min(400px, 65vh)",
                maxHeight: "65vh",
            },
            '&[data-title-multiline="true"]': {
                [mq(lg)]: {
                    minHeight: "min(400px, 55vh)",
                    maxHeight: "55vh",
                },
            },
            [`& .${reactPlayer} img`]: {
                borderRadius: "0 !important",
            },
            [`&[data-poster-visible] .${reactPlayer} img`]: {
                visibility: "hidden",
            },
            [`& .${reactPlayer} :global(.wistia_embed), & .${reactPlayer} :global(.w-video-wrapper)`]:
                {
                    borderRadius: 0,
                },
            [`& video.${reactPlayer}, & .${reactPlayer} video`]: {
                borderRadius: 0,
            },
        } satisfies CSSObject,
    } satisfies BoxProps,

    mediaFill: {
        css: {
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            zIndex: 2,
        } satisfies CSSObject,
    } satisfies BoxProps,

    mobileImage: {
        css: {
            display: "block",
            [mq(md)]: { display: "none" },
        } satisfies CSSObject,
    },

    desktopImageWithMobile: {
        css: {
            display: "none",
            [mq(md)]: { display: "block" },
        } satisfies CSSObject,
    },

    videoPoster: {
        css: {
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            zIndex: 3,
        } satisfies CSSObject,
    },

    content: {
        width: "100%",
        boxSizing: "border-box",
    } satisfies BoxProps,

    contentRow: {
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        justifyContent: "space-between",
        gap: space["8"],
        width: "100%",
        css: {
            [mq(md)]: {
                flexDirection: "row",
                alignItems: "flex-end",
                justifyContent: "space-between",
                gap: space["4.8"],
            },
        } satisfies CSSObject,
    } satisfies FlexProps,

    textColumn: {
        flexDirection: "column",
        flex: "1 1 auto",
        minWidth: 0,
        gap: space["2.4"],
        css: {
            [mq(lg)]: {
                gap: space["3"],
                maxWidth: "793px",
            },
        } satisfies CSSObject,
    } satisfies FlexProps,

    ctaColumn: {
        display: "flex",
        flexDirection: "column",
        gap: space["2.4"],
        width: "100%",
        alignItems: "stretch",
        css: {
            [mq(md)]: {
                alignItems: "flex-end",
                flexShrink: 0,
                width: "auto",
                '&[data-has-legal-text="true"]': {
                    justifyContent: "space-between",
                    minHeight: "137px",
                },
                "&:not([data-has-legal-text])": {
                    justifyContent: "flex-end",
                },
            },
            [mq(lg)]: {
                width: "auto",
                maxWidth: "none",
            },
        } satisfies CSSObject,
    } satisfies FlexProps,

    ctaRow: {
        flexDirection: "row",
        flexWrap: "wrap",
        gap: space["1.6"],
        width: "100%",
        maxWidth: "100%",
        justifyContent: "flex-start",
        css: {
            [mq(md)]: {
                flexWrap: "nowrap",
                width: "auto",
                maxWidth: "none",
                justifyContent: "flex-end",
            },
        } satisfies CSSObject,
    } satisfies FlexProps,

    title: {
        color: colors.black,
        fontWeight: "bold",
        fontSize: { base: "40px", xl: "56px" },
        lineHeight: { base: "48px", xl: "64px" },
        letterSpacing: "0.2px",
        css: {
            [mq(lg)]: {
                maxWidth: "805px",
            },
        } satisfies CSSObject,
    },

    description: {
        color: colors.black,
        fontWeight: 400,
        fontSize: fs.mobileBodyTextLarge,
        lineHeight: "24px",
        letterSpacing: "0.4px",
        maxWidth: "100%",
        marginBottom: 0,
        css: {
            [mq(md)]: {
                fontSize: space["3"],
                lineHeight: "23px",
            },
            [mq(lg)]: {
                fontSize: fs.mobileBodyTextLarge,
                lineHeight: "24px",
                maxWidth: "562px",
            },
        } satisfies CSSObject,
    } satisfies BoxProps,

    legalText: {
        color: colors.black,
        fontSize: {
            base: fs.mobileBodyB4,
            lg: fs.desktopBodyTextNormal,
        },
        marginTop: 0,
        css: {
            paddingTop: "18px",
            paddingBottom: "18px",
            [mq(md)]: {
                alignSelf: "flex-end",
                textAlign: "right",
                marginTop: 0,
            },
            "& a, & button[data-type='modal-link']": heroRichTextLinkStyle,
            "& p": { marginBottom: 0 },
        } satisfies CSSObject,
    } satisfies BoxProps,

    button: {
        display: "flex",
        height: "48px",
        minH: "48px",
        px: { base: space["3"], lg: space["4"] },
        justifyContent: "center",
        alignItems: "center",
        gap: space["3"],
        fontSize: { base: "14px", lg: "16px" },
        fontWeight: "bold",
        width: "unset",
        maxWidth: "100%",
        flex: "0 1 auto",
        textDecoration: "none",
        iconSpacing: 0,
        _hover: {
            textDecoration: "none",
        },
        css: {
            minWidth: 0,
            [mq(md)]: {
                flex: "0 0 auto",
                maxWidth: "none",
                paddingLeft: space["2.4"],
                paddingRight: space["2.4"],
            },
        } satisfies CSSObject,
    } satisfies Partial<ButtonProps>,
};
